// createSamplingEvent/actions.js

import {
  createSamplingEventUIInitialState,
  createSamplingEventInitialState,
} from "./createSamplingEventState";
import {
  getSamplingSiteInfo,
  saveSamplingEvent,
  getAllActiveSamplingEvents,
  getSamplingEventById,
  deleteSamplingEvent,
  getSamplingEquipmentMeters,
} from "../../api/SamplingEventsApi";

export const makeCreateSamplingEventUIActions = (set, get) => ({
  updateUI: (payload) =>
    set((s) => ({
      createSamplingEventUI: { ...s.createSamplingEventUI, ...payload },
    })),

  setWellFilters: (filters) =>
    set((s) => ({
      createSamplingEventUI: {
        ...s.createSamplingEventUI,
        wellFilters: filters,
        currentWellPage: 1,
      },
    })),

  setWellSort: (sort) =>
    set((s) => ({
      createSamplingEventUI: {
        ...s.createSamplingEventUI,
        wellSort: sort,
        currentWellPage: 1,
      },
    })),

  setCurrentWellPage: (page) =>
    set((s) => ({
      createSamplingEventUI: {
        ...s.createSamplingEventUI,
        currentWellPage: page,
      },
    })),

  setWellsPerPage: (size) =>
    set((s) => ({
      createSamplingEventUI: { ...s.createSamplingEventUI, wellsPerPage: size },
    })),

  toggleFilterMenu: (isOpen) =>
    set((s) => ({
      createSamplingEventUI: {
        ...s.createSamplingEventUI,
        isFilterMenuOpen:
          typeof isOpen === "boolean"
            ? isOpen
            : !s.createSamplingEventUI.isFilterMenuOpen,
      },
    })),

  resetFiltersAndSort: () =>
    set((s) => ({
      createSamplingEventUI: {
        ...s.createSamplingEventUI,
        wellFilters: createSamplingEventUIInitialState.wellFilters,
        wellSort: createSamplingEventUIInitialState.wellSort,
        currentWellPage: 1,
      },
    })),

  resetCreateSamplingEventUI: () =>
    set({ createSamplingEventUI: createSamplingEventUIInitialState }),
});

export const makeCreateSamplingEventActions = (set, get) => ({
  // Event Information setters
  setEventName: (value) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, eventName: value },
    })),
  setStartDate: (date) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, startDate: date },
    })),
  setEndDate: (date) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, endDate: date },
    })),
  setTeamLead: (employee) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, teamLead: employee },
    })),

  // Project information
  setNoProjectNumberChecked: (checked) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        noProjectNumberChecked: !!checked,
      },
    })),
  setSelectedProject: (project) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        selectedProject: project,
        availablePhases: project?.phase_list || [],
        selectedPhase: null,
        availableTasks: [],
        selectedTask: null,
      },
    })),
  setSelectedPhase: (phase) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        selectedPhase: phase,
        availableTasks: phase?.task_list || [],
        selectedTask: null,
      },
    })),
  setSelectedTask: (task) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, selectedTask: task },
    })),
  setProjectDescription: (text) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        projectDescription: text || "",
      },
    })),

  // Site & well info
  setNewSelectedSamplingSite: (site) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        newSelectedSamplingSite: site,
        // reset dependent selections
        selectedSiteWellInfo: null,
        selectedWellIds: [],
      },
    })),
  setSelectedSiteWellInfo: (info) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        selectedSiteWellInfo: info,
      },
    })),
  setSelectedWellIds: (idsOrUpdater) =>
    set((s) => {
      const previousIds = s.createSamplingEvent.selectedWellIds || [];
      const nextIds =
        typeof idsOrUpdater === "function"
          ? idsOrUpdater(previousIds)
          : Array.isArray(idsOrUpdater)
          ? idsOrUpdater
          : [];
      return {
        createSamplingEvent: {
          ...s.createSamplingEvent,
          selectedWellIds: nextIds,
        },
      };
    }),

  // Groups
  setSamplingGroups: (groups) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, samplingGroups: groups },
    })),
  setNextGroupId: (id) =>
    set((s) => ({
      createSamplingEvent: { ...s.createSamplingEvent, nextGroupId: id },
    })),

  // Equipment
  setSamplingEquipmentMeters: (meters) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        samplingEquipmentMeters: meters || [],
      },
    })),

  // Form errors
  setFormErrors: (errors) =>
    set((s) => ({
      createSamplingEvent: {
        ...s.createSamplingEvent,
        formErrors: errors || {},
      },
    })),

  resetCreateSamplingEventForm: () =>
    set({ createSamplingEvent: createSamplingEventInitialState }),

  // Server interactions (from utils)
  fetchSamplingWellSiteInfo: async (siteName) => {
    try {
      const response = await getSamplingSiteInfo(siteName);
      if (response?.status === 200) {
        get().createSamplingEventActions.setSelectedSiteWellInfo(response.data);
        // Reset dependent UI
        get().createSamplingEventActions.setSelectedWellIds([]);
        get().createSamplingEventUIActions.setCurrentWellPage(1);
        get().createSamplingEventUIActions.setWellFilters(
          createSamplingEventUIInitialState.wellFilters
        );
        get().createSamplingEventUIActions.setWellSort(
          createSamplingEventUIInitialState.wellSort
        );
        return { ok: true };
      }
      return { ok: false, error: response };
    } catch (error) {
      return { ok: false, error };
    }
  },

  fetchSamplingEquipmentMeters: async () => {
    try {
      const response = await getSamplingEquipmentMeters();
      if (response?.status === 200) {
        get().createSamplingEventActions.setSamplingEquipmentMeters(
          response.data?.sampling_equipment_meters || []
        );
        return { ok: true };
      }
      return { ok: false, error: response };
    } catch (error) {
      return { ok: false, error };
    }
  },

  // Validation port from utils
  validateSamplingEventForm: () => {
    const s = get().createSamplingEvent;
    const errors = {};

    if (!s.eventName || s.eventName.trim() === "")
      errors.eventName = "Event name is required";
    if (!s.startDate) errors.startDate = "Start date is required";
    if (!s.endDate) errors.endDate = "End date is required";
    if (s.startDate && s.endDate && s.startDate > s.endDate)
      errors.dateRange = "End date must be after start date";
    if (!s.teamLead) errors.teamLead = "Team lead is required";
    if (!s.newSelectedSamplingSite)
      errors.samplingSite = "Sampling site is required";
    if (!s.selectedProject) errors.project = "Project is required";
    if (s.selectedProject && !s.selectedPhase)
      errors.phase = "Phase is required";
    if (
      s.selectedPhase &&
      !s.selectedTask &&
      (s.availableTasks?.length || 0) > 0
    )
      errors.task = "Task is required";

    if ((s.samplingGroups || []).length === 0) {
      if (!errors.groups && !errors.duplicateGroups) {
        errors.groupsGeneral = "At least one sampling group is required";
      }
    } else {
      const groupNames = s.samplingGroups.map((g) => (g.name || "").trim());
      const counts = groupNames.reduce(
        (acc, n) => ((acc[n] = (acc[n] || 0) + 1), acc),
        {}
      );
      const duplicateNames = Object.keys(counts).filter((n) => counts[n] > 1);
      if (duplicateNames.length > 0)
        errors.duplicateGroups = `Group names must be unique. Duplicates found: ${duplicateNames.join(
          ", "
        )}`;

      const groupErrors = {};
      s.samplingGroups.forEach((group, index) => {
        const k = `group_${group.id || index}`;
        const cur = [];
        if (!group.name || group.name.trim() === "")
          cur.push("Group name cannot be empty.");
        if ((group.employees || []).length === 0)
          cur.push("At least one employee must be assigned.");
        if ((group.wells || []).length === 0)
          cur.push("At least one well must be assigned.");
        if (cur.length > 0) {
          const keyName =
            group.name && !duplicateNames.includes(group.name) ? group.name : k;
          groupErrors[keyName] = cur;
        }
      });
      if (Object.keys(groupErrors).length > 0) errors.groups = groupErrors;
    }

    get().createSamplingEventActions.setFormErrors(errors);
    return Object.keys(errors).length === 0;
  },

  // Server submit
  createSamplingEvent: async (user) => {
    const ok = get().createSamplingEventActions.validateSamplingEventForm();
    if (!ok) return { ok: false, error: "validation" };

    const s = get().createSamplingEvent;

    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const year = d.getFullYear();
      return `${month}-${day}-${year}`;
    };

    const payload = {
      eventID: "",
      samplingEventName: s.eventName,
      startDate: formatDate(s.startDate),
      endDate: formatDate(s.endDate),
      userID: user?.employee_id,
      samplingTeamLead:
        s.teamLead?.value ?? s.teamLead?.employee_id ?? s.teamLead,
      samplingSiteName: s.newSelectedSamplingSite?.value,
      projectNumber: s.selectedProject ? s.selectedProject.value : "",
      phase: s.selectedPhase
        ? s.selectedPhase.phase_id || s.selectedPhase.value
        : "",
      task: s.selectedTask
        ? s.selectedTask.task_id || s.selectedTask.value
        : "",
      groupData: (s.samplingGroups || []).map((group) => ({
        groupName: group.name,
        employees: (group.employees || []).map(
          (emp) => emp.employee_id ?? emp.value ?? emp
        ),
        wells: (group.wells || []).map((well) => ({
          well_id: well.well_id ?? well.id ?? well,
          assigned_group: group.name,
        })),
      })),
    };

    try {
      const response = await saveSamplingEvent(payload);
      if (response?.status === 200) {
        return { ok: true };
      }
      return { ok: false, error: response };
    } catch (error) {
      return { ok: false, error };
    }
  },

  resetCreateSamplingEventForm: () =>
    set({ createSamplingEvent: createSamplingEventInitialState }),
});
